import React, { useEffect, useState, useRef } from "react";
import Sidebar from "../Sidebar";
import SprzedazFilter from "./SprzedazFilter";
import axios from "axios";
import styled from "styled-components";
import { FaTable } from "react-icons/fa6";
import TopBar from "../TopBar";
import * as XLSX from "xlsx";

const Wrapper = styled.div`
  width: 100%;
  padding: 20px;
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
`;

const ScrollContainer = styled.div`
  padding-bottom: 16px;
  display: flex;
  flex-direction: column;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid #ccc;
  border-radius: 8px;
  background-color: #fff;
  font-size: 0.7rem;

  th,
  td {
    padding: 10px 14px;
    border-bottom: 1px solid #eee;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 240px;
  }

  th {
    background-color: #87ceeb;
    font-weight: 600;
    color: #333;
    cursor: pointer;
    position: sticky;
    top: 0;
    z-index: 2;
  }

  tr:nth-child(even) {
    background-color: #f9f9f9;
  }

  tr:hover {
    background-color: #eef6ff;
  }

  td.positive {
    color: green;
    font-weight: bold;
  }

  td.negative {
    color: red;
    font-weight: bold;
  }

  td.bold {
    font-weight: bold;
  }
`;

const PaginationControls = styled.div`
  margin-top: 0px;
  padding: 10px 0;
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;

  button {
    padding: 6px 12px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    border: 1px solid #87ceeb;
    background: #87ceeb;
    color: #fff;
    border-radius: 4px;
    transition: background 0.2s ease, transform 0.1s ease;

    &:hover:not(:disabled) {
      background-color: #62b2d6;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  span {
    align-self: center;
    font-size: 14px;
    font-weight: bold;
    color: #333;
  }
`;

// NEW computed columns list extended
const newColumns = [
  "Amount",
  "Vat Amount",
  "KodKrajuNadaniaTIN",
  "Domestic",
  "EU",
  "Export / Import",
  "TP Transaction",
  "Service",
  "ZW",
  "POZK",
  "POZK2",
  "ZERO",
  "ZERO2",
  "TT",
  "GTU_01",
  "GTU_02",
  "GTU_03",
  "GTU_04",
  "GTU_05",
  "GTU_06",
  "GTU_07",
  "GTU_08",
  "GTU_09",
  "GTU_10",
  "GTU_11",
  "GTU_12",
  "GTU_13",
];

const SpzedazData = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 18;

  const tableRef = useRef(null);

  const formatPLN = (value) => {
    const number = parseFloat(value?.toString().replace(/[^\d.-]/g, ""));
    if (isNaN(number)) return "-";
    return number.toLocaleString("pl-PL", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const hiddenColumns = [
    "id",
    "Hide",
    "kodSystemowy",
    "wersjaSchemy",
    "WariantFormularza",
    "DataWytworzeniaJPK",
    "NazwaSystemu",
    "CelZlozenia",
    "poz",
    "KodUrzedu",
    "rola",
    "Email",
    "DataSprzedazy",
    "TP",
    "GTU_05",
    "NIP",
    "PelnaNazwa",
  ];

  const formatValue = (column, value) => {
    if (
      ["NIP", "NrKontrahenta"].includes(column) &&
      typeof value === "number"
    ) {
      return value.toFixed(0);
    }
    return value || "-";
  };

  // UPDATED: computeAdditionalColumns – add ZW/POZK/POZK2/ZERO/ZERO2/TT/GTU + new IFs
  const computeAdditionalColumns = (row) => {
    const add = {
      Amount: "-",
      "Vat Amount": "-",
      Domestic: "✗",
      EU: "✗",
      "Export / Import": "✗",
      "TP Transaction": "✗",
      Service: "✗",
      ZW: "✗",
      POZK: "✗",
      POZK2: "✗",
      ZERO: "✗",
      ZERO2: "✗",
      TT: "✗",
      GTU_01: "✗",
      GTU_02: "✗",
      GTU_03: "✗",
      GTU_04: "✗",
      GTU_05: "✗",
      GTU_06: "✗",
      GTU_07: "✗",
      GTU_08: "✗",
      GTU_09: "✗",
      GTU_10: "✗",
      GTU_11: "✗",
      GTU_12: "✗",
      GTU_13: "✗",
    };

    // Amount – extended with K_12, K_13, K_14
    if (row.K_15) add.Amount = `${row.K_15} PLN`;
    else if (row.K_19) add.Amount = `${row.K_19} PLN`;
    else if (row.K_10) add.Amount = `${row.K_10} PLN`;
    else if (row.K_21) add.Amount = `${row.K_21} PLN`;
    else if (row.K_17) add.Amount = `${row.K_17} PLN`;
    else if (row.K_11) add.Amount = `${row.K_11} PLN`;
    else if (row.K_22) add.Amount = `${row.K_22} PLN`;
    else if (row.K_12) add.Amount = `${row.K_12} PLN`;
    else if (row.K_13) add.Amount = `${row.K_13} PLN`;
    else if (row.K_14) add.Amount = `${row.K_14} PLN`;

    // VAT Amount – unchanged
    if (row.K_16) add["Vat Amount"] = `${row.K_16} PLN`;
    else if (row.K_20) add["Vat Amount"] = `${row.K_20} PLN`;
    else if (row.K_18) add["Vat Amount"] = `${row.K_18} PLN`;

    // Old Domestic / EU / Export / TP logic (kept)
    if (row.K_15 || row.K_17 || row.K_19 || row.K_10) add.Domestic = "✓";
    if (row.K_21 || row.K_11) add.EU = "✓";
    if (row.K_22) add["Export / Import"] = "✓";

    if (row.TP === "1") add["TP Transaction"] = "✓";

    // Service FIX: remove K_17 and K_21 as Service
    // New rule below: K_12 sets Service

    // NEW IFs (K_10 – K_14, TT, GTU)
    const hasK10 = !!row.K_10;
    const hasK11 = !!row.K_11;
    const hasK12 = !!row.K_12;
    const hasK13 = !!row.K_13;
    const hasK14 = !!row.K_14;

    // If K=10 then ZW = True and Domestic = true
    if (hasK10) {
      add.ZW = "✓";
      add.Domestic = "✓";
    }

    // If K=11 then POZK = true and Domestic = false
    if (hasK11) {
      add.POZK = "✓";
      add.Domestic = "✗";
    }

    // If K=12 then POZK2 = true and Domestic = false
    // If K=12 then EU = true and Service = true
    if (hasK12) {
      add.POZK2 = "✓";
      add.Domestic = "✗";
      add.EU = "✓";
      add.Service = "✓";
    }

    // If K=13 then ZERO=true and Domestic = true
    if (hasK13) {
      add.ZERO = "✓";
      add.Domestic = "✓";
    }

    // If K=14 then ZERO2=true and Domestic = true
    if (hasK14) {
      add.ZERO2 = "✓";
      add.Domestic = "✓";
    }

    // TT flags
    if (row.TT_WNT === "1" || row.TT_D === "1") {
      add.TT = "✓";
    }

    // GTU_01 ... GTU_13 flags
    for (let i = 1; i <= 13; i++) {
      const key = `GTU_${String(i).padStart(2, "0")}`;
      if (row[key] && row[key].toString() === "1") {
        add[key] = "✓";
      }
    }

    return add;
  };

const fetchData = async () => {
  try {
    const res = await axios.get(
      "https://server229256.nazwa.pl/api/sprzedaz",
      {
        headers: { Authorization: localStorage.getItem("token") },
      }
    );

    // 1) Enrich with computed columns
    const enriched = res.data.map((row) => ({
      ...row,
      ...computeAdditionalColumns(row),
    }));

    // 2) DROP rows where BOTH Amount AND Vat Amount are empty / "-"
    const enrichedVisible = enriched.filter((row) => {
      const hasAmount =
        row.Amount && row.Amount !== "-" && row.Amount !== "";
      const hasVat =
        row["Vat Amount"] &&
        row["Vat Amount"] !== "-" &&
        row["Vat Amount"] !== "";
      return hasAmount || hasVat; // keep row if at least one is present
    });

    // 3) Use only filtered rows in the view
    setData(enrichedVisible);
    setFilteredData(enrichedVisible);

    if (enrichedVisible.length > 0) {
      let cols = Object.keys(enrichedVisible[0]).filter(
        (c) => !hiddenColumns.includes(c) && !c.startsWith("K_")
      );
      const idx = cols.indexOf("DataWystawienia");
      newColumns.forEach((c) => {
        if (!cols.includes(c)) {
          if (idx !== -1) cols.splice(idx + 1, 0, c);
          else cols.push(c);
        }
      });
      setColumns(cols);
    }
  } catch (err) {
    console.error("Error fetching:", err);
  }
};


  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDataFiltered = (newFiltered) => {
    setFilteredData(newFiltered);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(filteredData.length / recordsPerPage);
  const currentRecords = filteredData.slice(
    (currentPage - 1) * recordsPerPage,
    currentPage * recordsPerPage
  );

  const handleSort = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === "ascending"
        ? "descending"
        : "ascending";

    setSortConfig({ key, direction });

    const sorted = [...filteredData].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];

      if (["Amount", "Vat Amount"].includes(key)) {
        const parseValue = (v) => {
          if (!v || typeof v !== "string") return 0;
          return parseFloat(
            v.replace(/\s/g, "").replace(",", ".").replace(/[^\d.-]/g, "")
          );
        };
        aVal = parseValue(aVal);
        bVal = parseValue(bVal);
      }

      if (aVal < bVal) return direction === "ascending" ? -1 : 1;
      if (aVal > bVal) return direction === "ascending" ? 1 : -1;
      return 0;
    });

    setFilteredData(sorted);
  };

  const getArrow = (key) => {
    if (sortConfig.key === key)
      return sortConfig.direction === "ascending" ? " ▲" : " ▼";
    return "";
  };

  const columnLabels = {
    KodFormularza: "Kod",
    Rok: "Rok",
    Miesiac: "Miesiac",
    LpSprzedazy: "LP",
    KodKrajuNadaniaTIN: "Kraj",
    NrKontrahenta: "NrKontrahenta",
    NazwaKontrahenta: "Kontrahenta",
    DowodSprzedazy: "Sprzedazy",
    DataWystawienia: "Data Wystawienia",
    Amount: "Kwota",
    "Vat Amount": "VAT",
    Domestic: "Krajowe",
    EU: "EU",
    "Export / Import": "Eksport/Import",
    "TP Transaction": "TP",
    Service: "Usługi",
    ZW: "ZW",
    POZK: "POZK",
    POZK2: "POZK2",
    ZERO: "ZERO",
    ZERO2: "ZERO2",
    TT: "TT",
    GTU_01: "GTU_01",
    GTU_02: "GTU_02",
    GTU_03: "GTU_03",
    GTU_04: "GTU_04",
    GTU_05: "GTU_05",
    GTU_06: "GTU_06",
    GTU_07: "GTU_07",
    GTU_08: "GTU_08",
    GTU_09: "GTU_09",
    GTU_10: "GTU_10",
    GTU_11: "GTU_11",
    GTU_12: "GTU_12",
    GTU_13: "GTU_13",
  };

  // Table columns extended with new boolean fields
  const tableColumns = [
    "KodFormularza",
    "Rok",
    "Miesiac",
    "LpSprzedazy",
    "KodKrajuNadaniaTIN",
    "NrKontrahenta",
    "NazwaKontrahenta",
    "DowodSprzedazy",
    "DataWystawienia",
    "Amount",
    "Vat Amount",
    "Domestic",
    "EU",
    "Export / Import",
    "TP Transaction",
    "Service",
    "ZW",
    "POZK",
    "POZK2",
    "ZERO",
    "ZERO2",
    "TT",
    "GTU_01",
    "GTU_02",
    "GTU_03",
    "GTU_04",
    "GTU_05",
    "GTU_06",
    "GTU_07",
    "GTU_08",
    "GTU_09",
    "GTU_10",
    "GTU_11",
    "GTU_12",
    "GTU_13",
  ];

  const parseAmountForExcel = (val) => {
  if (val == null) return null;

  // It will handle things like:
  // "1 032,00 PLN", "1032,00", "1032.00", "1032"
  const cleaned = String(val)
    .replace(/\s/g, "")          // remove spaces / non-breaking spaces
    .replace(/[^\d,.,-]/g, "")   // keep digits, dot, minus, comma
    .replace(",", ".");          // use dot as decimal separator

  const num = parseFloat(cleaned);
  if (Number.isNaN(num)) return null;
  return num;
};

const exportToExcel = () => {
  if (!filteredData || !filteredData.length) {
    alert("No data to export.");
    return;
  }

  // Build rows from the data, not from the DOM table
  const excelRows = filteredData.map((row) => ({
    KodFormularza: row.KodFormularza,
    Rok: row.Rok,
    Miesiac: row.Miesiac,
    LpSprzedazy: row.LpSprzedazy,
    KodKrajuNadaniaTIN: row.KodKrajuNadaniaTIN,
    NrKontrahenta: row.NrKontrahenta,
    NazwaKontrahenta: row.NazwaKontrahenta,
    DowodSprzedazy: row.DowodSprzedazy,
    DataWystawienia: row.DataWystawienia,

    // 🔧 FIXED: numeric values in zł
    Kwota: parseAmountForExcel(row.Amount),
    VAT: parseAmountForExcel(row["Vat Amount"]),

    Domestic: row.Domestic,
    EU: row.EU,
    ExportImport: row["Export / Import"],
    TP: row["TP Transaction"],
    Service: row.Service,
  }));

  const worksheet = XLSX.utils.json_to_sheet(excelRows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "SprzedazDane");

  XLSX.writeFile(
    workbook,
    `sprzedaz_dane_${new Date().toISOString().slice(0, 10)}.xlsx`
  );
};


  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <div style={{ flexShrink: 0 }}>
        <Sidebar />
      </div>

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <TopBar />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "10px 20px 0",
            fontSize: "1.2rem",
            fontWeight: "bold",
            color: "#87CEEB",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          <FaTable />
          Sprzedaż / Dane Źródłowe
        </div>

        <Wrapper>
          <SprzedazFilter
            allData={data}
            filteredData={filteredData}
            onDataFiltered={handleDataFiltered}
            onExportToExcel={exportToExcel}
          />

          <ScrollContainer>
            <div style={{ overflowX: "auto" }}>
              <PaginationControls>
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  Pierwsza
                </button>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                >
                  Poprzednia
                </button>
                <span>
                  Strona {currentPage} z {totalPages}
                </span>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                >
                  Następna
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Ostatnia
                </button>
              </PaginationControls>

              <StyledTable ref={tableRef}>
                <thead>
                  <tr>
                    {tableColumns.map((col) => (
                      <th key={col} onClick={() => handleSort(col)}>
                        {columnLabels[col] || col}
                        {getArrow(col)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentRecords.map((row, rowIdx) => (
                    <tr key={rowIdx}>
                      {tableColumns.map((col) => {
                        const val = row[col];
                        let className = "";
                        if (newColumns.includes(col)) {
                          if (val === "✓") className = "positive";
                          else if (val === "✗") className = "negative";
                          else className = "bold";
                        }

                        return (
                          <td key={col} className={className}>
                            {["Amount", "Vat Amount"].includes(col) ? (
                              <strong>{formatPLN(val)}</strong>
                            ) : newColumns.includes(col) ? (
                              <span
                                className={
                                  val === "✓"
                                    ? "positive"
                                    : val === "✗"
                                    ? "negative"
                                    : "bold"
                                }
                              >
                                {val}
                              </span>
                            ) : (
                              formatValue(col, val)
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </StyledTable>
            </div>
          </ScrollContainer>
        </Wrapper>
      </div>
    </div>
  );
};

export default SpzedazData;
